/**
 * The editor's failure banner: what went wrong, and the one way back.
 *
 * The save state itself belongs to the editor bar's pill, which is always on
 * screen and holds its own space. This said the same thing a second time, in
 * a full-width band that appeared on every save and moved the page under the
 * reader mid-edit, and announced each transition twice through a second live
 * region. So it says nothing about saving while saving is working; the one
 * thing it says then is that a background read failed, which the pill cannot.
 *
 * @module components/editor/EditorSyncStatus
 */

import React from "react";

import { Button, ErrorAlert, Text, WarningAlert } from "@/components/ui";
import type { EditorProgrammeSyncValue } from "@/platform";
import { isRevisionConflict } from "@/platform/editor-sync-reducer";

type SyncStatus = EditorProgrammeSyncValue["status"];

export const EDITOR_SYNC_STATUS_COPY: Record<SyncStatus, string> = {
  idle: "Choose a programme above to open its backend record.",
  loading: "Opening programme from the platform...",
  "read-only": "Programme opened read-only.",
  "saved-locally": "Changes are waiting to save to the platform.",
  "saving-backend": "Saving changes to the platform...",
  "saved-backend": "Programme is saved to the platform.",
  offline: "You are offline. Changes have not reached the platform.",
  reconnecting: "Connection restored. Retrying your changes...",
  locked: "This programme is currently being edited by someone else.",
  conflict: "This programme changed elsewhere. Reload the backend version before editing again.",
  unauthorized: "This account cannot save changes to the selected backend record.",
  error: "The platform could not complete this programme operation.",
};

/** The states a reader has to act on. Everything else is the pill's job. */
const PROBLEM_STATUSES = new Set<SyncStatus>([
  "conflict",
  "locked",
  "unauthorized",
  "offline",
  "error",
]);

const MODULE_CHANGED = /^Module "([^"]+)" in programme "[^"]+" has changed since you opened it/;
/** Every programme refusal the server words names the record by id first. */
const PROGRAMME_REFUSAL = /^Programme \S+ (.+?)\.?$/;

/**
 * The server's detail in the reader's words, or nothing where the sentence
 * above already says it. The server names records by id, which nobody reading
 * this knows them by.
 */
function refusalDetail(
  error: Error,
  moduleTitle: (moduleId: string) => string | undefined,
): string | undefined {
  if (isRevisionConflict(error)) {
    return undefined;
  }
  const { message } = error;
  const changed = MODULE_CHANGED.exec(message);
  if (changed) {
    return `${moduleTitle(changed[1]) ?? "A module"} has changed since you opened it.`;
  }
  const refused = PROGRAMME_REFUSAL.exec(message);
  return refused ? `This programme ${refused[1]}.` : message;
}

export function BackendEditorStatus({
  sync,
  moduleTitle = () => undefined,
}: {
  sync: EditorProgrammeSyncValue;
  moduleTitle?: (moduleId: string) => string | undefined;
}): React.ReactElement | null {
  if (!PROBLEM_STATUSES.has(sync.status)) {
    // Nothing typed is unsaved for it, so it says so beside the pill rather than as a failure.
    return sync.behind && sync.record ? (
      <WarningAlert data-testid="backend-editor-status">
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <Text as="span" className="min-w-0">
            The latest copy could not be read, so what is shown may be behind the platform.
          </Text>
          <Button
            variant="outline"
            size="sm"
            icon="arrow-clockwise"
            onClick={() => sync.reloadProgramme()}
          >
            Read again
          </Button>
        </div>
      </WarningAlert>
    ) : null;
  }

  const showReload =
    (Boolean(sync.record) || sync.status === "locked") &&
    (sync.status === "conflict" || sync.status === "locked");
  const showRetry = (sync.status === "error" || sync.status === "offline") && Boolean(sync.record);
  const Band = sync.status === "conflict" || sync.status === "error" ? ErrorAlert : WarningAlert;
  const detail = sync.error ? refusalDetail(sync.error, moduleTitle) : undefined;

  return (
    <Band data-testid="backend-editor-status">
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Text as="span" className="font-semibold">
            {EDITOR_SYNC_STATUS_COPY[sync.status]}
          </Text>
          {detail ? (
            <Text size="sm" tone="muted" className="mt-1">
              {detail}
            </Text>
          ) : null}
        </div>
        {showReload || showRetry ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {showReload ? (
              <Button
                variant="outline"
                size="sm"
                icon="arrow-clockwise"
                onClick={() => sync.reloadProgramme({ discardLocal: true })}
              >
                Reload backend version
              </Button>
            ) : null}
            {showRetry ? (
              <Button variant="primary" size="sm" icon="arrow-clockwise" onClick={sync.retrySave}>
                Retry save
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Band>
  );
}
